import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Envelope, PaperPlaneTilt } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { submitContact } from '@/lib/api';

const ContactSection = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [captchaError, setCaptchaError] = useState(false);

  // Generate new CAPTCHA numbers
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: '' });
    setCaptchaError(false);
  };

  // Initialize CAPTCHA on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      setCaptchaError(true);
      toast.error('Please solve the CAPTCHA correctly.');
      generateCaptcha();
      return;
    }
    
    setLoading(true);
    try {
      await submitContact(contactForm);
      toast.success('Message sent successfully!');
      setContactForm({ name: '', email: '', message: '' });
      generateCaptcha();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 sm:py-20 bg-muted/50" data-testid="home-contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Left side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-wine-600 font-medium text-sm uppercase tracking-wider mb-4 block">Get in Touch</span>
            <h2 className="font-serif text-4xl md:text-5xl text-primary mb-6">
              Contact <span className="italic text-wine-600">Us</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Have questions about wine tasting deals, the app, or want to partner with us? We'd love to hear from you!
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-wine-100 flex items-center justify-center">
                  <Envelope size={24} className="text-wine-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email Us</p>
                  <a href="mailto:info@wineryfinderapp.com" className="text-primary font-medium hover:text-wine-600 transition-colors">
                    info@wineryfinderapp.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Right side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="min-w-0"
          >
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 border border-gray-100">
              <h3 className="font-serif text-2xl text-primary mb-6">Send Us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                  <Input 
                    type="text" 
                    placeholder="John Doe"
                    value={contactForm.name} 
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})} 
                    required 
                    className="w-full"
                    data-testid="home-contact-name" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <Input 
                    type="email" 
                    placeholder="john@example.com"
                    value={contactForm.email} 
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})} 
                    required 
                    className="w-full"
                    data-testid="home-contact-email" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    className="w-full min-h-[120px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-wine-500 focus:border-transparent" 
                    placeholder="How can we help you?"
                    value={contactForm.message} 
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})} 
                    required 
                    data-testid="home-contact-message" 
                  />
                </div>
                
                {/* CAPTCHA */}
                <div className={`p-4 rounded-lg ${captchaError ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Security Check
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="bg-white px-4 py-2 rounded-lg border border-gray-300 font-mono text-lg font-bold text-gray-700">
                      {captcha.num1} + {captcha.num2} = ?
                    </div>
                    <Input 
                      type="number" 
                      placeholder="Answer"
                      value={captcha.answer} 
                      onChange={(e) => {
                        setCaptcha({...captcha, answer: e.target.value});
                        setCaptchaError(false);
                      }} 
                      required 
                      className={`w-24 ${captchaError ? 'border-red-500' : ''}`}
                      data-testid="home-contact-captcha" 
                    />
                    <button 
                      type="button" 
                      onClick={generateCaptcha}
                      className="text-wine-600 hover:text-wine-700 text-sm underline"
                    >
                      New question
                    </button>
                  </div>
                  {captchaError && (
                    <p className="text-red-500 text-sm mt-2">Incorrect answer. Please try again.</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-wine-600 hover:bg-wine-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                  data-testid="home-contact-submit"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <PaperPlaneTilt size={20} weight="fill" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
